import type { Normalizer } from '@change-corgi/fcm/normalizers';
import { createShapeNormalizer } from '@change-corgi/fcm/normalizers';

import { defineFcm } from '../shared/factory';

const assertAmounts = ({
	amounts,
	minAmount,
	maxAmount,
	defaultAmount,
	minSliderAmount,
	maxSliderAmount,
	sliderStep,
}: {
	amounts: readonly number[];
	minAmount: number;
	maxAmount: number;
	defaultAmount: number;
	minSliderAmount?: number;
	maxSliderAmount?: number;
	sliderStep?: number;
	// eslint-disable-next-line complexity
}) => {
	if (amounts.length !== 4) {
		throw new Error('Amounts must have 4 amounts');
	}
	if (minAmount > Math.min(...amounts)) {
		throw new Error('minAmount must be less than or equal to minimum available amount');
	}

	if (maxAmount < Math.max(...amounts)) {
		throw new Error('maxAmount must be greater than or equal to maximum available amount');
	}

	if (defaultAmount < minAmount || defaultAmount > maxAmount)
		throw new Error('defaultAmount must be in between min and max amount');

	if (minSliderAmount !== undefined && minSliderAmount < minAmount)
		throw new Error('minSliderAmount must be greater than or equal to minAmount');

	if (maxSliderAmount !== undefined && maxSliderAmount > maxAmount)
		throw new Error('maxSliderAmount must be less than or equal to maxAmount');

	if (minSliderAmount !== undefined && maxSliderAmount !== undefined && minSliderAmount > maxSliderAmount)
		throw new Error('minSliderAmount must be less than or equal to maxSliderAmount');

	if (sliderStep !== undefined && sliderStep <= 0) throw new Error('sliderStep must be a positive number');
};

const subscriptionConfigurationShapeNormalizer = createShapeNormalizer({
	type: 'object',
	shape: {
		amounts: {
			type: 'array',
			shape: {
				type: 'number',
			},
		},
		minAmount: { type: 'number' },
		maxAmount: { type: 'number' },
		defaultAmount: { type: 'number' },
		thirdPartySubscriptionService: { type: 'string', optional: true },
		subscriptionCancellationSurveyId: { type: 'string', optional: true },
		disableFeedbackForm: { type: 'boolean', optional: true },
		minSliderAmount: { type: 'number', optional: true },
		maxSliderAmount: { type: 'number', optional: true },
		sliderStep: { type: 'number', optional: true },
		sliderLayout: { type: 'enum', values: ['labelsInline', 'labelsBelow'] as const, optional: true },
	},
});

const normalizer: Normalizer<ReturnType<typeof subscriptionConfigurationShapeNormalizer>> = (value) => {
	const subscriptionConfiguration = subscriptionConfigurationShapeNormalizer(value);
	const { amounts, minAmount, maxAmount, defaultAmount, minSliderAmount, maxSliderAmount, sliderStep } =
		subscriptionConfiguration;
	assertAmounts({
		amounts,
		minAmount,
		maxAmount,
		defaultAmount,
		minSliderAmount,
		maxSliderAmount,
		sliderStep,
	});
	return subscriptionConfiguration;
};

export const FCM_SUBSCRIPTION_CONFIGURATION = defineFcm('subscription_configuration')
	.asJson(normalizer)
	.withDefault(undefined)
	.toConfig({
		owner: 'squad-koi',
		scope: 'corgi-front-end',
		context: 'Subscription',
		description: `Country specific subscription configuration data to replace subscriptionCountries.json in fe-config

All amount fields (amounts, minAmount, maxAmount, defaultAmount, minSliderAmount, maxSliderAmount) are
in base units (e.g. cents for USD). They are converted to display units by the app before rendering.

Slider fields (all optional):
- minSliderAmount: minimum value the slider can be dragged to (must be >= minAmount). In base units (cents).
- maxSliderAmount: maximum value the slider can be dragged to (must be <= maxAmount). In base units (cents).
- sliderLayout: 'labelsInline' (labels beside the slider) or 'labelsBelow' (labels beneath)
- sliderStep: the increment used when dragging the slider (must be a positive number). sliderStep is in DISPLAY units (e.g. dollars for USD), NOT base units (cents)
  IMPORTANT: confirm this value is correct before saving. A wrong step will cause the slider to
  snap to amounts that don't align with the preset buttons. Example: sliderStep=1 means $1 increments.

	{
		amounts: [300, 500, 1000, 2000],
		minAmount: 300,
		maxAmount: 20000,
		defaultAmount: 500,
		thirdPartySubscriptionService: '', // optional
		subscriptionCancellationSurveyId: '', // optional
		disableFeedbackForm: false // optional
		minSliderAmount: 300, // optional, in base units (cents)
		maxSliderAmount: 20000, // optional, in base units (cents)
		sliderStep: 1, // optional, in DISPLAY units (dollars) — e.g. 1 = $1 steps
		sliderLayout: 'labelsInline' // optional, can be 'labelsInline' or 'labelsBelow'
	}`,
		template: {
			amounts: [300, 500, 1000, 2000],
			minAmount: 300,
			maxAmount: 20000,
			defaultAmount: 500,
			thirdPartySubscriptionService: '',
			subscriptionCancellationSurveyId: '',
			disableFeedbackForm: false,
			minSliderAmount: 300,
			maxSliderAmount: 20000,
			sliderStep: 1,
			sliderLayout: 'labelsInline',
		},
	});
